import type dayjs from 'dayjs'
import { inject, provide, toRefs, reactive, readonly, computed } from 'vue'
import { useRouter } from 'vue-router'

import useAxiosResponse from '@/composables/common/useAxiosResponse'
import useToast from '@/composables/feedback/useToast'
import useLocalization from '@/composables/localization/useLocalization'
import bookingArmService from '@/services/api/booking'
import bookingServiceArmService from '@/services/api/booking-service'
import bookingVisitDatetimeArmService from '@/services/api/booking-visit-datetime'
import doctorArmService from '@/services/api/doctor'
import roomArmService from '@/services/api/room'
import { type BookingService, type BookingVisitTime } from '@/types/Booking'
import { type Doctor } from '@/types/Doctor'
import { type AdmissionRoom } from '@/types/Room'
import { dateYearMonthDayFormat } from '@/utils/date'

export type BookingState = 'form' | 'confirmation'

export interface State {
  bookingState: BookingState
  serviceSelected: BookingService | null
  dateSelected: dayjs.Dayjs | null
  timeSelected: BookingVisitTime | null
  symptoms: string
  doctorSelected: Doctor
  isRequireRoomForAdmission: boolean
  firstName: string
  lastName: string
  email: string
  phone: string
  insurance: string
  policyNumber: string
  isPatientVip: boolean
  admissionRoomSelected: AdmissionRoom | null
  admissionRoomStartDate: dayjs.Dayjs | null
  admissionRoomEndDate: dayjs.Dayjs | null
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const useBaseBookingView = () => {
  const router = useRouter()
  const { addToast } = useToast()
  const { localeTexts } = useLocalization()
  const NO_SPECIFIC_DOCTOR: Doctor = {
    id: '',
    name_en: localeTexts.value['create-booking-step1-doctor-no-text'],
    name_th: localeTexts.value['create-booking-step1-doctor-no-text'],
    image_url: ''
  }

  const state = reactive<State>({
    bookingState: 'form',
    serviceSelected: null,
    dateSelected: null,
    timeSelected: null,
    symptoms: '',
    doctorSelected: NO_SPECIFIC_DOCTOR,
    isRequireRoomForAdmission: true,
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    insurance: '',
    policyNumber: '',
    isPatientVip: false,
    admissionRoomSelected: null,
    admissionRoomStartDate: null,
    admissionRoomEndDate: null
  })

  const allServicesQuery = useAxiosResponse(async () => {
    return await bookingServiceArmService.fetchAllServices({ requestErrorEnabled: true })
  }, {
    loadingMode: 'normal'
  })

  const allAdmissionRoomsQuery = useAxiosResponse(async () => {
    return await roomArmService.fetchAllAdmissionRooms({ requestErrorEnabled: true })
  }, {
    loadingMode: 'normal'
  })

  const bookingVisitTimesQuery = useAxiosResponse(async () => {
    return await bookingVisitDatetimeArmService.fetchBookingVisitTimes({ requestErrorEnabled: true })
  }, {
    loadingMode: 'normal'
  })

  const allDoctorsQuery = useAxiosResponse(async () => {
    return await doctorArmService.fetchAllDoctors({ requestErrorEnabled: true })
  }, {
    loadingMode: 'normal'
  })

  const createBookingMutation = useAxiosResponse(async () => {
    return await bookingArmService.createBooking(
      {
        service_id: state.serviceSelected!.id,
        visit_date: dateYearMonthDayFormat(state.dateSelected!),
        visit_time: state.timeSelected!.id,
        symptoms: state.symptoms.trim() === '' ? undefined : state.symptoms,
        doctor_id: state.doctorSelected.id === '' ? undefined : state.doctorSelected.id,
        room_id: !state.isRequireRoomForAdmission || state.admissionRoomSelected === null ? undefined : state.admissionRoomSelected.id,
        room_start_date: !state.isRequireRoomForAdmission ? undefined : dateYearMonthDayFormat(state.admissionRoomStartDate!),
        room_end_date: !state.isRequireRoomForAdmission ? undefined : dateYearMonthDayFormat(state.admissionRoomEndDate!),
        patient_first_name: state.firstName,
        patient_last_name: state.lastName,
        patient_phone: state.phone,
        patient_email: state.email.trim() === '' ? undefined : state.email,
        insurance_company: state.insurance.trim() === '' ? undefined : state.insurance,
        policy_number: state.policyNumber.trim() === '' ? undefined : state.policyNumber,
        is_patient_vip: state.isPatientVip
      },
      { requestErrorEnabled: true }
    )
  }, {
    onSuccess () {
      addToast({
        type: 'common',
        message: localeTexts.value['create-booking-confirm-step3-success-toast'],
        second: 5
      })
      router.push('/')
    },
    loadingMode: 'normal'
  })

  // #region Getters

  const isVisitDetailsCompleted = computed((): boolean => {
    const isRoomForAdmissionCompleted = state.isRequireRoomForAdmission
      ? state.admissionRoomStartDate !== null && state.admissionRoomEndDate !== null
      : true

    return isRoomForAdmissionCompleted &&
      state.serviceSelected !== null &&
      state.dateSelected !== null &&
      state.timeSelected !== null
  })

  const isPatientDetailsCompleted = computed((): boolean => {
    return state.firstName.trim() !== '' &&
      state.lastName.trim() !== '' &&
      state.phone.trim() !== ''
  })

  const bookingCompletedStep = computed((): number => {
    if (isPatientDetailsCompleted.value && isVisitDetailsCompleted.value) {
      return 2
    } else if (isVisitDetailsCompleted.value) {
      return 1
    }

    return 0
  })

  const admissionRoomSelectedIndex = computed((): number => {
    if (state.admissionRoomSelected === null || allAdmissionRoomsQuery.data.value === undefined) {
      return 0
    }

    const index = allAdmissionRoomsQuery.data.value?.findIndex((admissionRoom) => admissionRoom.id === state.admissionRoomSelected?.id)

    return index > -1 ? index : 0
  })

  const allDoctors = computed((): Doctor[] => {
    if (allDoctorsQuery.data.value === undefined) {
      return [NO_SPECIFIC_DOCTOR]
    }

    return [NO_SPECIFIC_DOCTOR, ...allDoctorsQuery.data.value]
  })

  const bookingSteps = computed(() => [
    localeTexts.value['create-booking-step-item-first-text'],
    localeTexts.value['create-booking-step-item-second-text'],
    localeTexts.value['create-booking-step-item-third-text']
  ])

  // #endregion

  // #region Actions

  const setBookingState = (value: BookingState): void => {
    state.bookingState = value
  }

  const setServiceSelected = (value: BookingService | null): void => {
    state.serviceSelected = value
  }

  const setDateSelected = (value: dayjs.Dayjs): void => {
    state.dateSelected = value
  }

  const setTimeSelected = (value: BookingVisitTime): void => {
    state.timeSelected = value
  }

  const setSymptoms = (value: string): void => {
    state.symptoms = value
  }

  const setDoctorSelected = (value: Doctor): void => {
    state.doctorSelected = value
  }

  const setIsRequireRoomForAdmission = (value: boolean): void => {
    state.isRequireRoomForAdmission = value
  }

  const setFirstName = (value: string): void => {
    state.firstName = value
  }

  const setLastName = (value: string): void => {
    state.lastName = value
  }

  const setEmail = (value: string): void => {
    state.email = value
  }

  const setPhone = (value: string): void => {
    state.phone = value
  }

  const setInsurance = (value: string): void => {
    state.insurance = value
  }

  const setPolicyNumber = (value: string): void => {
    state.policyNumber = value
  }

  const setAdmissionRoomSelected = (value: AdmissionRoom): void => {
    if (state.admissionRoomSelected?.id === value.id) {
      state.admissionRoomSelected = null
    } else {
      state.admissionRoomSelected = value
    }
  }

  const setAdmissionRoomStartDate = (value: dayjs.Dayjs | null): void => {
    state.admissionRoomStartDate = value
  }

  const setAdmissionRoomEndDate = (value: dayjs.Dayjs | null): void => {
    state.admissionRoomEndDate = value
  }

  const setIsPatientVip = (value: boolean): void => {
    state.isPatientVip = value
  }

  // #endregion

  return {
    ...toRefs(readonly(state)),
    allServicesQuery,
    allAdmissionRoomsQuery,
    bookingVisitTimesQuery,
    allDoctorsQuery,
    createBookingMutation,
    // Gatters
    bookingCompletedStep,
    isVisitDetailsCompleted,
    isPatientDetailsCompleted,
    admissionRoomSelectedIndex,
    allDoctors,
    bookingSteps,
    // Actions
    setBookingState,
    setServiceSelected,
    setDateSelected,
    setTimeSelected,
    setSymptoms,
    setDoctorSelected,
    setIsRequireRoomForAdmission,
    setFirstName,
    setLastName,
    setEmail,
    setPhone,
    setInsurance,
    setPolicyNumber,
    setAdmissionRoomSelected,
    setAdmissionRoomStartDate,
    setAdmissionRoomEndDate,
    setIsPatientVip
  }
}

export const defaultKey = Symbol('view:booking')

export default function useBookingView (): ReturnType<typeof useBaseBookingView> {
  const result = inject<ReturnType<typeof useBaseBookingView> | undefined>(defaultKey, undefined)

  if (result === undefined) {
    const newResult = useBaseBookingView()
    provide(defaultKey, newResult)
    return newResult
  }

  return result
}
